import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import usePartnerOffer from '~/campaigns/hooks/usePartnerOffer'
import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import { useGetStripePricesQuery } from '~/subscription/apis/subscription.api'
import { StripePrice } from '~/subscription/types/subscription.types'
import { formatToLocalCurrency } from '~/subscription/util'
import { isKnowItPrice } from '~/subscription/utils/withoutKnowItPrices'
import { AppLanguage } from '~/types/locale'

interface Props {
  partnerDetails: PartnerDetailsType
  className?: string
}

interface BadgeCopy {
  prefix?: string
  value: string
  label: string
  pill: string
}

const isFamilyMonthly = (price?: StripePrice): boolean =>
  price?.metadata?.subscriptionType === 'family' && price?.recurring?.interval === 'month' && !isKnowItPrice(price)

/** The partner's offer as a tilted ticket in the landing hero: free months, one discount, or "up to" two. */
export default function PartnerOfferBadge({ partnerDetails, className = '' }: Props): React.ReactElement | null {
  const { t } = useTranslation('campaign', { keyPrefix: 'offerBadge' })
  const language = useRouter().locale as AppLanguage
  const offer = usePartnerOffer(partnerDetails, 1)
  const { data: geoPrices } = useGetStripePricesQuery({ excludeDnb: true })
  const geoMonthly: StripePrice | undefined = geoPrices?.stripeGeoLocationPricesWithCouponDetails
    ?.map((entry: { price: StripePrice }) => entry.price)
    .find(isFamilyMonthly)

  const monthlyBase = offer.monthly.base ?? (geoMonthly?.tiers?.[0]?.unit_amount ?? 0) / 100
  const currency = offer.monthly.base != null ? offer.currency : geoMonthly?.currency
  const priceLabel = (amount: number): string => formatToLocalCurrency(currency ?? '', amount, language).priceLabel

  const monthlyOff = offer.monthly.percentOff ?? 0
  const yearlyOff = offer.yearly.isHidden ? monthlyOff : offer.yearly.percentOff ?? 0
  const maxOff = Math.max(monthlyOff, yearlyOff)

  const copy: BadgeCopy | undefined = offer.monthly.freeMonths
    ? {
        value: String(offer.monthly.freeMonths),
        label: t('monthsFree', { count: offer.monthly.freeMonths }),
        pill: t('thenPrice', { price: priceLabel(monthlyBase) }),
      }
    : maxOff && monthlyOff === yearlyOff
    ? {
        value: `${monthlyOff}%`,
        label: t('off'),
        pill: t('nowPrice', { price: priceLabel(monthlyBase * (1 - monthlyOff / 100)) }),
      }
    : maxOff
    ? { prefix: t('upTo'), value: `${maxOff}%`, label: t('discount'), pill: t('pickPlan') }
    : undefined

  // Hidden until the price it quotes has loaded, so the pill never shows a zero.
  if (!partnerDetails.showOfferBadge || !copy || !monthlyBase || offer.isLoading) return null

  const isStacked = copy.label.includes('\n')

  return (
    <div className={classNames('rotate-7 select-none', className)}>
      <div className="relative rounded-20 border-1.5 border-dashed border-white p-1.5">
        <div
          className={classNames(
            'flex items-center gap-1.5 rounded-14 bg-gradient-partner-badge pl-7 pr-5.5 pt-4 text-white drop-shadow-partner-badge',
            copy.prefix ? 'pb-8.5' : 'pb-5.5',
          )}
        >
          <div className="flex flex-col whitespace-nowrap leading-none">
            {copy.prefix && (
              <span className="-mb-1 text-20px font-extrabold tracking-0.04em text-green-lime-soft">{copy.prefix}</span>
            )}
            <span className="text-7xl font-black">{copy.value}</span>
          </div>
          {isStacked ? (
            <span className="whitespace-pre-line text-22px font-extrabold leading-0.95">{copy.label}</span>
          ) : (
            <span className="flex w-5.5 items-center justify-center self-stretch">
              <span className="-rotate-90 whitespace-nowrap text-22px font-extrabold leading-0.95">{copy.label}</span>
            </span>
          )}
        </div>
        <p className="absolute -bottom-3.5 left-14 whitespace-nowrap rounded-full border-2.5 border-purple-badge-ink bg-green-lime-soft px-5 py-2 text-20px font-extrabold leading-none text-purple-badge-ink">
          {copy.pill}
        </p>
      </div>
    </div>
  )
}
